/**
 * Clinical note records linked to an appointment — the "Linked notes" rows inside the
 * appointment modal's Notes section.
 *
 * Body only: no SectionShell. It lives inside the modal's single "Notes" section
 * alongside the three free-text fields (the appointment-type note, this appointment's
 * own `reason`, and a patient demographic answer), so there is one place for anything
 * note-related rather than two sections with near-identical names.
 *
 * In its own file because UnifiedAppointmentModal is already ~5k lines. The list data
 * is owned by the modal (it needs the count for the section header badge and the
 * auto-expand effect) and passed in; this component owns the picker, the link/unlink
 * mutations, and the refetch-on-note-close signal.
 *
 * Links are stored server-side against the appointment's stable `auditRecordID`, so
 * they survive the audit-versioned save that mints a new `scheduledAppointmentsID`.
 * The `appointmentId` prop is the current live PK and is what the endpoints take.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip, Plus, Unlink } from 'lucide-react';
import { toast } from '@/store/toastStore';

import { SearchableCombobox, type ComboboxOption } from '@/components/ui/SearchableCombobox';
import { appointmentsService, type AppointmentLinkedNote } from '@/services/appointments.service';
import { getErrorMessage } from '@/services/apiClient';
import * as notesSvc from '@/v5/services/notes.service';
import { useNoteForm } from '@/v5/contexts/NoteFormContext';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { pacificToWallClock } from '@/lib/facilityTimezone';

interface AppointmentLinkedNotesProps {
  /** Current live appointment PK; the server resolves it through the audit chain. */
  appointmentId: number;
  /** The appointment's currently-attached patient — scopes the picker. */
  patientId: number;
  /** Chip label forwarded into the note form, e.g. "Consult · Tue, Aug 12, 2:30 PM". */
  appointmentLabel: string;
  /** Owned by the modal so the section header badge and auto-expand can read the count. */
  notes: AppointmentLinkedNote[];
  loading: boolean;
  /** The modal's handleAddNote — already carries appointment context. */
  onAddNote: () => void;
}

/** `noteDate` comes back as a Pacific "YYYY-MM-DD HH:MM:SS" string. */
function fmtNoteDate(noteDate: string | null, userTz: string): string {
  if (!noteDate || noteDate.startsWith('0000-00-00')) return '—';

  const [datePart, timePart = '00:00:00'] = noteDate.split(' ');
  const wall = pacificToWallClock(datePart, timePart.slice(0, 5), userTz);
  const [y, mo, d] = wall.date.split('-').map(Number);
  if (!y || !mo || !d) return '—';

  // Noon anchor so the label can't slip a day via the local timezone.
  return new Date(y, mo - 1, d, 12).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function AppointmentLinkedNotes({
  appointmentId,
  patientId,
  appointmentLabel,
  notes,
  loading,
  onAddNote,
}: AppointmentLinkedNotesProps) {
  const qc = useQueryClient();
  const noteForm = useNoteForm();
  const userTz = useUserTimezone();
  const [busyNoteId, setBusyNoteId] = useState<number | null>(null);

  // Candidates for the picker. Kept here rather than in the modal because only the
  // picker needs them.
  const { data: candidates = [], isLoading: candidatesLoading } = useQuery({
    queryKey: ['appt-note-candidates', patientId],
    queryFn: () => notesSvc.listNotes(String(patientId)),
    enabled: !!patientId,
    staleTime: 30_000,
  });

  const invalidate = useCallback(() => {
    // Same key the modal's query uses, so lifting the list up did not break this.
    void qc.invalidateQueries({ queryKey: ['appt-linked-notes', appointmentId] });
    void qc.invalidateQueries({ queryKey: ['appt-note-candidates', patientId] });
  }, [qc, appointmentId, patientId]);

  // Refetch when a note opened from THIS appointment closes. Mirrors the close-signal
  // pattern in NotesSection rather than storing a callback on OpenNote, which would
  // keep an unmounted modal's closure alive. Fires on discard too — one wasted
  // refetch, the same trade NotesSection accepts.
  const hadOursRef = useRef(false);
  const current = noteForm.current;
  useEffect(() => {
    // The null/appointmentId guards matter: comparing linkAppointmentId alone is true
    // when both are undefined and would fire spuriously.
    const isOurs =
      current != null && appointmentId != null && current.linkAppointmentId === appointmentId;
    if (isOurs) {
      hadOursRef.current = true;
    } else if (hadOursRef.current) {
      hadOursRef.current = false;
      invalidate();
    }
  }, [current, appointmentId, invalidate]);

  const linkedIds = useMemo(() => notes.map((n) => n.noteId), [notes]);

  const candidateOptions = useMemo<ComboboxOption<number>[]>(
    () =>
      candidates
        .filter((n) => n.active !== 0)
        .map((n) => ({
          value: n.noteId,
          label: n.title || `Note #${n.noteId}`,
          // `meta` is searched by default, so date/type/author are all typeable.
          meta: [fmtNoteDate(n.noteDate, userTz), n.typeName, n.authorName]
            .filter(Boolean)
            .join(' · '),
        })),
    [candidates, userTz],
  );

  async function handleLink(noteId: number) {
    setBusyNoteId(noteId);
    try {
      await appointmentsService.linkAppointmentNote(appointmentId, noteId);
      invalidate();
      toast.success('Note linked');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to link note'));
    } finally {
      setBusyNoteId(null);
    }
  }

  async function handleUnlink(noteId: number) {
    // No confirm: unlinking leaves the note intact and is two clicks to redo. Matches
    // handleRemovePlan in the modal, which also skips the confirm that
    // handleRemovePatient uses for genuinely destructive removal.
    setBusyNoteId(noteId);
    try {
      await appointmentsService.unlinkAppointmentNote(appointmentId, noteId);
      invalidate();
      toast.success('Note unlinked');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to unlink note'));
    } finally {
      setBusyNoteId(null);
    }
  }

  function openLinkedNote(row: AppointmentLinkedNote) {
    noteForm.openNote({
      // MUST be the row's own patient, not the prop: the appointment's patient can be
      // swapped after linking, and getNote() is patient-scoped so the prop would 404.
      patientId: String(row.patientId),
      noteId: row.noteId,
      linkAppointmentId: appointmentId,
      linkAppointmentLabel: appointmentLabel,
    });
  }

  return (
    <>
      <div className="mb-2 flex items-center justify-between gap-2">
        <label className="text-xs font-semibold text-slate-600">Linked notes</label>
        <button
          type="button"
          onClick={onAddNote}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-blue-600 hover:bg-blue-50 hover:text-blue-800"
        >
          <Plus className="h-3.5 w-3.5" />
          New
        </button>
      </div>

      <div className="mb-2">
        <SearchableCombobox<number>
          mode="multi-add"
          closeOnAdd
          loading={candidatesLoading}
          options={candidateOptions}
          addedValues={linkedIds}
          placeholder="Search this patient's notes to link…"
          emptyMessage="No notes for this patient"
          onAdd={(opt) => void handleLink(opt.value)}
        />
      </div>

      {loading ? (
        <p className="text-xs italic text-slate-400">Loading…</p>
      ) : notes.length === 0 ? (
        <p className="text-xs italic text-slate-400">No notes linked to this appointment.</p>
      ) : (
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-slate-200 text-[10px] font-medium uppercase tracking-wide text-slate-500">
              <th className="py-1.5 pr-2 text-left">Date</th>
              <th className="py-1.5 px-2 text-left">Type</th>
              <th className="py-1.5 px-2 text-left">Subject</th>
              <th className="py-1.5 px-2 text-left">By</th>
              <th className="py-1.5 pl-2 text-right sr-only">Unlink</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {notes.map((row) => {
              const isDeleted = row.active === 0;
              return (
                <tr
                  key={row.noteId}
                  onClick={() => openLinkedNote(row)}
                  className={`cursor-pointer hover:bg-slate-50 ${isDeleted ? 'opacity-50' : ''}`}
                  title="Open this note"
                >
                  <td className="py-1.5 pr-2 whitespace-nowrap text-slate-500">
                    {fmtNoteDate(row.noteDate, userTz)}
                  </td>
                  <td className="py-1.5 px-2 text-slate-600">{row.typeName || '—'}</td>
                  <td className="py-1.5 px-2 font-medium text-slate-800">
                    <span className="inline-flex items-center gap-1">
                      {row.title || `Note #${row.noteId}`}
                      {isDeleted ? (
                        <span className="text-[10px] font-normal italic text-slate-400">
                          (deleted)
                        </span>
                      ) : null}
                      {row.attachmentCount > 0 ? (
                        <Paperclip className="h-3 w-3 shrink-0 text-slate-400" />
                      ) : null}
                      {/* The appointment's patient was changed after this link was
                          made. Flagged rather than filtered out, so the row can still
                          be opened and unlinked. */}
                      {row.patientMismatch ? (
                        <span
                          className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700"
                          title="This note belongs to a different patient than the appointment's current patient"
                        >
                          other patient
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 max-w-[110px] truncate text-slate-600">
                    {row.authorName || '—'}
                  </td>
                  <td className="py-1.5 pl-2 text-right">
                    <button
                      type="button"
                      title="Unlink from this appointment"
                      aria-label={`Unlink note ${row.noteId}`}
                      disabled={busyNoteId === row.noteId}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleUnlink(row.noteId);
                      }}
                      className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                    >
                      <Unlink className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}
